import { useTheme } from '@material-ui/core';
import React, { useMemo } from 'react';
import { components } from 'react-select';
import LoadingIndicator from '~components/react-select/loading-indicator';
import Typography from '~components/typography';
import hexToRGBA from '~utils/hex-to-rgba';
import { contentHeaderSelectComponents } from './content-header-select-props';
import { useHeaderSticky } from './use-header-sticky';

export const useContentHeaderSelectStyles = () => {
  const theme = useTheme();
  const headerSticky = useHeaderSticky();

  return useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        ...(headerSticky
          ? { backgroundColor: theme.colors.brandBg, boxShadow: 'none' }
          : { boxShadow: theme.shadow.componentShallow }),
        border: 'none',
        borderRadius: theme.borderRadius.full,
        paddingLeft: 12,
        paddingRight: 12,
        minHeight: 32,
        minWidth: 180,
        ':hover': {
          border: 'none',
        },
        ':active': {
          border: 'none',
        },
        cursor: 'pointer',
      }),
      dropdownIndicator: () => ({
        marginLeft: 2,
        fontSize: 10,
        color: theme.colors.brandMedium,
      }),
      group: () => ({
        marginTop: 8,
      }),
      groupHeading: () => ({
        ...theme.typography.caption,
        display: 'inline-block',
        color: theme.colors.grey66,
        marginLeft: 8,
        marginRight: 8,
        marginBottom: 0,
        padding: '1px 8px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        backgroundColor: theme.colors.greyE8,
        borderRadius: theme.borderRadius.full,
      }),
      menu: (provided) => ({
        ...provided,
        boxShadow: `0 2px 8px 0 ${hexToRGBA(theme.colors.dark, 0.16)}`,
        marginBottom: 2,
        marginTop: 2,
        width: 'auto',
        minWidth: '100%',
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: 328,
      }),
      option: (provided, state) => ({
        ...provided,
        ...theme.typography.dropdown,
        ':active': { backgroundColor: 'inherit' },
        padding: '6px 12px',
        color: state.isSelected
          ? theme.colors.brandAction
          : state.isDisabled
          ? theme.colors.grey99
          : theme.colors.dark,
        backgroundColor: state.isFocused ? theme.colors.brandBg : provided.background,
        whiteSpace: 'nowrap',
      }),
      placeholder: (provided) => ({
        ...provided,
        ...theme.typography.h6,
        color: theme.colors.grey99,
      }),
      singleValue: (provided) => ({
        ...provided,
        ...theme.typography.h6,
        maxWidth: 'auto',
        overflow: 'visible',
        margin: 0,
        color: theme.colors.brandIcon,
        position: 'relative',
        top: 'unset',
        transform: 'none',
      }),
    }),
    [
      headerSticky,
      theme.borderRadius.full,
      theme.colors.brandAction,
      theme.colors.brandBg,
      theme.colors.brandIcon,
      theme.colors.brandMedium,
      theme.colors.dark,
      theme.colors.grey99,
      theme.colors.greyE8,
      theme.colors.grey66,
      theme.shadow.componentShallow,
      theme.typography.caption,
      theme.typography.dropdown,
      theme.typography.h6,
    ]
  );
};

const Option = (props) => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <span>{data.name}</span>
        {data.city?.name && (
          <Typography themeColor="grey99" style={{ marginLeft: 8 }}>
            {data.city.name}
          </Typography>
        )}
      </div>
    </components.Option>
  );
};

export const contentHeaderPropertySelectComponents = {
  Control: contentHeaderSelectComponents.Control,
  DropdownIndicator: contentHeaderSelectComponents.DropdownIndicator,
  LoadingIndicator,
  Option,
  IndicatorSeparator: null,
};
